/**
 * Lines of one sale document that are the same line: same article, same
 * designation (as the MCF receives it), unit price, discount, tax group,
 * PSVB group and levy parameters (TSB, specific tax). Entry merges them
 * into one line whose quantity and discount are the sums, so the document
 * total is unchanged; a line without an article is never merged.
 *
 * @module @efacture/fiscal-core/services/line-merge
 */
import { mcfItemName } from "./mcf-item-names.js";
import { roundMoney } from "./tax-calculator.js";
/** The identity of a line for merging; null for a line without an article. */
export function lineMergeKey(line) {
    if (!line.articleId)
        return null;
    const tax = line.specificTax ?? null;
    return JSON.stringify([
        line.articleId,
        mcfItemName(line.articleName),
        roundMoney(line.unitPrice),
        roundMoney(line.discount ?? 0),
        line.taxGroup,
        line.psvbGroup || null,
        line.isBeverage === true,
        tax === null
            ? null
            : tax.mode === "AD_VALOREM"
                ? [tax.mode, tax.rate, tax.label]
                : [tax.mode, tax.unitAmount, tax.label],
    ]);
}
/** A quantity sum kept to the thousandth of a unit. */
function addQuantities(a, b) {
    return Math.round((a + b) * 1000) / 1000;
}
/**
 * The lines with every later copy of a line merged into its first
 * occurrence: quantities and discounts summed, the first line's other
 * fields kept, the order of first occurrences kept.
 */
export function mergeDuplicateLines(lines) {
    const merged = [];
    const byKey = new Map();
    for (const line of lines) {
        const key = lineMergeKey(line);
        const at = key === null ? undefined : byKey.get(key);
        if (at === undefined) {
            if (key !== null)
                byKey.set(key, merged.length);
            merged.push(line);
            continue;
        }
        const first = merged[at];
        const discount = roundMoney((first.discount ?? 0) + (line.discount ?? 0));
        merged[at] = {
            ...first,
            quantity: addQuantities(first.quantity, line.quantity),
            ...(first.discount !== undefined || line.discount !== undefined ? { discount } : {}),
        };
    }
    return merged;
}
//# sourceMappingURL=line-merge.js.map